import type { Config } from '@puckeditor/core';

import {
  COMMERCE_BLOCK_NAMES,
  NAV_BLOCK_NAMES,
  COMMERCE_BLOCKS,
  PAGE_BLOCKS,
  PAGE_CONTENT_BLOCK_NAMES,
  PAGE_LAYOUT_BLOCK_NAMES,
} from '../components/blocks';
import { faqConfig } from '../components/faq';
import { PathField } from '../fields';
import { createPageRootConfig } from '../components/page-root-config';

// -- Config --

/** Puck editor configuration for the standalone page builder. */
export const standalonePagePuckConfig: Config = {
  categories: {
    layout: {
      title: 'Layout',
      components: [...PAGE_LAYOUT_BLOCK_NAMES, 'Faq'],
    },
    content: {
      title: 'Content',
      components: PAGE_CONTENT_BLOCK_NAMES,
    },
    carousels: {
      title: 'Carousels',
      components: [...COMMERCE_BLOCK_NAMES, ...NAV_BLOCK_NAMES],
    },
  },
  root: createPageRootConfig({
    fields: {
      // A plain text field (not a custom widget like urlPath/PathField -- no format to validate)
      // so the page's display name can be edited at any time, independent of its variantKey/URL
      // slug. Seeded once from VariantNameModal's typed display name on first publish (see
      // `CmsEditorShell.handleModalConfirm`); editable here afterwards. Read via `getName(data)`
      // in `standalone-page-editor/editor.tsx` and sent as the pages route's required `name`
      // field alongside `urlPath` on every save/publish.
      name: {
        type: 'text',
        label: 'Page Name',
        ai: { exclude: true },
      },
      urlPath: {
        type: 'custom',
        label: 'URL Path',
        render: PathField,
        ai: {
          stream: false,
          schema: { type: 'string', pattern: '^/[a-z0-9]+(?:[/\\-_][a-z0-9]+)*/?$' },
          instructions:
            'Relative URL path for this page. Must start with /. Lowercase letters, numbers, hyphens, underscores only. e.g. /about, /deals/summer-2026',
        },
      },
    },
    defaultProps: { name: '', urlPath: '' },
    ai: {
      instructions:
        "You are editing a flexible standalone page for a cannabis dispensary storefront.\n\nA standalone page can serve many purposes, including:\n- a brand-led homepage or introduction to the dispensary;\n- a promotional, seasonal, event, or campaign destination;\n- an informational page about the business, shopping experience, services, or locations;\n- an educational or frequently-asked-questions resource;\n- a policy, accessibility, legal, or other trust-focused page using retailer-approved content;\n- another focused experience described by the retailer.\n\nUnderstand the retailer's goal, intended shopper, and existing content before choosing an approach. Use the available components to create a coherent page that fits that purpose. A page might emphasize brand storytelling, product or category discovery, a focused call to action, detailed reading, imagery, questions and answers, or a combination of these.\n\nDo not force every page into the same layout or require a Hero, carousel, Faq, or call to action when it does not support the page's purpose. Favor clear hierarchy, readable pacing, and an obvious sense of what the shopper can learn or do next.\n\nFor policy or legal-oriented pages, organize only retailer-supplied or approved language; do not invent legal requirements, guarantees, licenses, or compliance claims. Do not invent business facts, destinations, products, promotions, or image URLs.",
    },
  }),
  components: { ...PAGE_BLOCKS, ...COMMERCE_BLOCKS, Faq: faqConfig },
};
